import { useRef, useState, useCallback, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single global audio instance - shared across ALL pages
let globalAudio = null;

// Anti-skip-storm guard: if the player skips too many tracks in a short window
// (e.g. on mobile when autoplay is blocked), stop instead of looping forever.
let recentSkipTimes = [];
const SKIP_WINDOW_MS = 8000;
const MAX_SKIPS_IN_WINDOW = 4;
function recordSkipAndShouldAbort() {
  const now = Date.now();
  recentSkipTimes = recentSkipTimes.filter((t) => now - t < SKIP_WINDOW_MS);
  recentSkipTimes.push(now);
  return recentSkipTimes.length > MAX_SKIPS_IN_WINDOW;
}
function resetSkipCounter() {
  recentSkipTimes = [];
}

// Global queue of musics; when current ends, next plays automatically
let globalQueue = [];
let globalQueueIndex = 0;
let radioMode = true; // when true, auto-loads next random CD when queue ends

// Radio scope: controls WHAT to load next when the current queue ends in radioMode.
//   { type: "all" }                              → any CD from any artist (default, home radio)
//   { type: "artist", artistName: "Zé Cantor" }  → next CD from SAME artist first;
//                                                  if exhausted, falls back to { type: "all" }.
let radioScope = { type: "all" };

// Preloaded next track (Audio element kept in memory to pre-buffer)
let preloadAudio = null;
let preloadUrl = null;

// Remember recently-played CD ids to avoid immediate repeats in radio mode
const recentCDIds = [];
const MAX_RECENT = 5;
// Remember recently-played ARTIST names to diversify radio (avoid 3 CDs do mesmo artista em sequência)
const recentArtists = [];
const MAX_RECENT_ARTISTS = 3;

// User intent: pra distinguir "pausa do usuário" vs "interrupção do sistema (call, Siri, etc)"
// Em iOS Safari, quando rola uma ligação, audio.pause() é disparado pelo SO.
// Quando termina, NÃO retoma automaticamente — precisa de auto-resume.
let userWantsPlay = false;
let lastPauseReason = null; // "user" | "interruption" | null

// Global stop function - stops any audio/video playing anywhere
const stopCallbacks = new Set();
const playingChangeCallbacks = new Set();
const progressCallbacks = new Set();

function emitProgress(currentTime, duration) {
  progressCallbacks.forEach((cb) => {
    try { cb(currentTime, duration); } catch { /* ignore */ }
  });
}

function emitPlayingChange(playingId, music) {
  playingChangeCallbacks.forEach((cb) => {
    try { cb(playingId, music); } catch { /* ignore */ }
  });
}

// ========= Media Session API =========
// Enables background audio + lock-screen / notification controls on mobile
function updateMediaSession(music) {
  if (!("mediaSession" in navigator) || !music) return;
  try {
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: music.title || "",
      artist: music.artist_name || "",
      album: music.album || music.artist_name || "",
      artwork: music.artist_image ? [
        { src: music.artist_image, sizes: "96x96", type: "image/jpeg" },
        { src: music.artist_image, sizes: "192x192", type: "image/jpeg" },
        { src: music.artist_image, sizes: "512x512", type: "image/jpeg" },
      ] : [],
    });
    navigator.mediaSession.setActionHandler("play", () => {
      userWantsPlay = true;
      lastPauseReason = null;
      if (globalAudio) globalAudio.play().catch(()=>{});
    });
    navigator.mediaSession.setActionHandler("pause", () => {
      userWantsPlay = false;
      lastPauseReason = "user";
      if (globalAudio) globalAudio.pause();
    });
    navigator.mediaSession.setActionHandler("previoustrack", () => playPrev());
    navigator.mediaSession.setActionHandler("nexttrack", () => playNext());
    navigator.mediaSession.setActionHandler("seekto", (d) => {
      if (d.seekTime != null) seekGlobal(d.seekTime);
    });
    navigator.mediaSession.setActionHandler("seekbackward", (d) => {
      if (globalAudio) seekGlobal((globalAudio.currentTime || 0) - (d.seekOffset || 10));
    });
    navigator.mediaSession.setActionHandler("seekforward", (d) => {
      if (globalAudio) seekGlobal((globalAudio.currentTime || 0) + (d.seekOffset || 10));
    });
  } catch (e) {
    // Media Session unsupported on some browsers; ignore
  }
}

// ========= Preload =========
// Keep an invisible <audio preload=auto> for the next track so it's buffered
function preloadNext(queue, idx) {
  const next = queue?.[idx + 1];
  if (!next?.preview_url) {
    if (preloadAudio) { try { preloadAudio.src = ""; } catch {} preloadAudio = null; preloadUrl = null; }
    return;
  }
  if (preloadUrl === next.preview_url) return; // already preloading
  if (preloadAudio) { try { preloadAudio.src = ""; } catch {} }
  preloadAudio = new Audio();
  preloadAudio.preload = "auto";
  try {
    preloadAudio.setAttribute("playsinline", "true");
    preloadAudio.setAttribute("webkit-playsinline", "true");
  } catch {}
  preloadAudio.src = next.preview_url;
  preloadUrl = next.preview_url;
  // No play(), just keep it loading in background
}

// ========= Radio Mode (auto-next CD) =========
// Cache the light CD list (sem tracks) para não bater no backend a cada faixa.
let cdListCache = null;
let cdListCachedAt = 0;
const CD_LIST_TTL_MS = 60_000; // 1 minuto

// Race-safe: guarda a promise em andamento; múltiplas chamadas concorrentes
// aguardam a mesma promise ao invés de retornar null.
let fetchRandomCDPromise = null;

async function _fetchRandomCDInner() {
  // 1) Pega lista LEVE (só ids, artist_name, cover) — cacheada por 60s
  const now = Date.now();
  if (!cdListCache || (now - cdListCachedAt) > CD_LIST_TTL_MS) {
    const { data } = await axios.get(`${API}/cd-submissions/public?limit=500`, { timeout: 15000 });
    if (!Array.isArray(data) || data.length === 0) return null;
    cdListCache = data;
    cdListCachedAt = now;
  }
  const data = cdListCache;

  const currentCD = globalQueue?.[globalQueueIndex]?._source_cd_id;

  // ==== ARTIST-SCOPED RADIO ====
  // If we're in "artist scope" (album/artist page), prefer another CD from the SAME artist first.
  // Only when the artist has no more CDs to serve, transition to general radio so the user
  // stays engaged on the platform.
  let picked = null;
  if (radioScope?.type === "artist" && radioScope.artistName) {
    const artistLC = radioScope.artistName.toLowerCase();
    const sameArtistPool = data.filter(
      (c) => (c.artist_name || "").toLowerCase() === artistLC
    );
    const excludeCurrent = new Set([currentCD, ...recentCDIds].filter(Boolean));
    const freshFromArtist = sameArtistPool.filter((c) => !excludeCurrent.has(c.id));
    if (freshFromArtist.length > 0) {
      picked = freshFromArtist[Math.floor(Math.random() * freshFromArtist.length)];
    } else {
      // Artist catalog exhausted → transition to general radio (keeps user engaged)
      radioScope = { type: "all" };
    }
  }

  if (!picked) {
    // ==== GENERAL RADIO (default) ====
    const excludeCDs = new Set([currentCD, ...recentCDIds].filter(Boolean));
    const excludeArtists = new Set(recentArtists.map((a) => (a || "").toLowerCase()));
    // First try: exclude both CDs and artists (best diversification)
    let candidates = data.filter((c) =>
      !excludeCDs.has(c.id) && !excludeArtists.has((c.artist_name || "").toLowerCase())
    );
    // Fallback: relax artist filter if no candidates (catálogo pequeno)
    if (candidates.length === 0) {
      candidates = data.filter((c) => !excludeCDs.has(c.id));
    }
    // Last fallback: only exclude current CD
    if (candidates.length === 0) {
      candidates = data.filter((c) => c.id !== currentCD);
    }
    const pool = candidates.length > 0 ? candidates : data;
    picked = pool[Math.floor(Math.random() * pool.length)];
  }

  if (!picked) return null;
  // 2) Detalhe do CD escolhido (com tracks) — endpoint separado, payload pequeno
  try {
    const { data: detail } = await axios.get(`${API}/cd-submissions/${picked.id}`, { timeout: 15000 });
    return detail;
  } catch {
    // se detalhe falhar, invalida cache e tenta outro na próxima chamada
    cdListCache = null;
    return null;
  }
}

async function fetchRandomCD() {
  if (fetchRandomCDPromise) return fetchRandomCDPromise;
  fetchRandomCDPromise = _fetchRandomCDInner();
  try {
    return await fetchRandomCDPromise;
  } catch {
    return null;
  } finally {
    fetchRandomCDPromise = null;
  }
}

async function loadRandomCDAndPlay() {
  const cd = await fetchRandomCD();
  if (!cd || !Array.isArray(cd.tracks) || cd.tracks.length === 0) {
    // No more CDs available — stop
    globalAudio = null;
    globalQueue = [];
    globalQueueIndex = 0;
    emitPlayingChange(null, null);
    return;
  }
  recentCDIds.unshift(cd.id);
  while (recentCDIds.length > MAX_RECENT) recentCDIds.pop();
  if (cd.artist_name) {
    recentArtists.unshift(cd.artist_name);
    while (recentArtists.length > MAX_RECENT_ARTISTS) recentArtists.pop();
  }

  const tracks = [...cd.tracks].sort((a, b) => (a.track_number || 0) - (b.track_number || 0));
  const queue = tracks
    .map((t, i) => {
      const src = t.preview_url || t.audio_url || "";
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      return {
        id: `cd-${cd.id}-t-${i}`,
        title: t.title || `Faixa ${t.track_number || i + 1}`,
        artist_name: cd.artist_name,
        artist_image: cd.cover_url ? `${apiBase}${cd.cover_url}` : null,
        preview_url: src && !src.startsWith("http") ? `${apiBase}${src}` : src,
        _source_cd_id: cd.id,
      };
    })
    .filter((t) => t.preview_url);
  if (queue.length === 0) {
    // CD has no playable tracks — skip to another
    loadRandomCDAndPlay();
    return;
  }
  playInternal(queue[0], queue, 0);
}

export function stopAllMedia() {
  if (globalAudio) {
    globalAudio.pause();
    globalAudio = null;
  }
  if (preloadAudio) { try { preloadAudio.src = ""; } catch {} preloadAudio = null; preloadUrl = null; }
  globalQueue = [];
  globalQueueIndex = 0;
  if ("mediaSession" in navigator) {
    try { navigator.mediaSession.metadata = null; } catch {}
  }
  emitPlayingChange(null, null);
  stopCallbacks.forEach((cb) => cb());
}

export function setRadioMode(enabled) {
  radioMode = !!enabled;
}

// Sets the current radio scope. Call this BEFORE starting playback to tell the player
// what to auto-load when the current queue ends.
//   setRadioScope({ type: "all" })                            → general radio (home)
//   setRadioScope({ type: "artist", artistName: "Xand" })     → artist-scoped radio
export function setRadioScope(scope) {
  if (scope && scope.type === "artist" && scope.artistName) {
    radioScope = { type: "artist", artistName: String(scope.artistName) };
  } else {
    radioScope = { type: "all" };
  }
}

// Returns the current scope (read-only) — useful for the UI to show "playing radio of X" hints.
export function getRadioScope() {
  return { ...radioScope };
}

// ========= iOS Interruption Auto-Resume =========
// Em iOS Safari, quando rola uma ligação ou o Siri ativa, audio.pause() é chamado pelo SO.
// Quando termina, NÃO retoma automaticamente — registramos listeners pra resumir.
let interruptionListenersInstalled = false;

function tryResumeAfterInterruption(source = "unknown") {
  if (!userWantsPlay) return;
  if (!globalAudio) {
    // Audio foi nulificado — tenta restaurar do último estado da queue
    const cur = globalQueue[globalQueueIndex];
    if (cur?.preview_url) {
      console.info(`[player] resume(${source}): re-playing queue item`);
      playInternal(cur, globalQueue, globalQueueIndex);
    }
    return;
  }
  if (globalAudio.paused) {
    console.info(`[player] resume(${source}): calling play()`);
    globalAudio.play().then(() => {
      const cur = globalQueue[globalQueueIndex];
      emitPlayingChange(cur?.id || null, cur || null);
    }).catch((err) => {
      console.warn("[player] resume blocked:", err?.message);
    });
  }
}

function installInterruptionListeners() {
  if (interruptionListenersInstalled || typeof document === "undefined") return;
  interruptionListenersInstalled = true;

  // 1) visibilitychange — quando aba volta a ser visível (depois de minimizar, voltar de outro app)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      // Pequeno delay pra iOS estabilizar o audio session após retorno
      setTimeout(() => tryResumeAfterInterruption("visibility"), 300);
    }
  });

  // 2) pageshow — quando navegação BFCache traz a página de volta (iOS depois de ligação)
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) {
      // Veio do BFCache (Back-Forward Cache) — costuma acontecer em iOS após interrupção
      setTimeout(() => tryResumeAfterInterruption("pageshow"), 300);
    }
  });

  // 3) focus do window — quando o app/aba ganha foco
  window.addEventListener("focus", () => {
    setTimeout(() => tryResumeAfterInterruption("focus"), 300);
  });
}
// Instala assim que o módulo carrega
installInterruptionListeners();

export function registerStopCallback(cb) {
  stopCallbacks.add(cb);
  return () => stopCallbacks.delete(cb);
}

export function registerPlayingChangeCallback(cb) {
  playingChangeCallbacks.add(cb);
  return () => playingChangeCallbacks.delete(cb);
}

export function registerProgressCallback(cb) {
  progressCallbacks.add(cb);
  return () => progressCallbacks.delete(cb);
}

export function seekGlobal(seconds) {
  if (globalAudio && Number.isFinite(seconds)) {
    try {
      globalAudio.currentTime = Math.max(0, Math.min(seconds, globalAudio.duration || seconds));      emitProgress(globalAudio.currentTime, globalAudio.duration || 0);
      if ("mediaSession" in navigator && globalAudio.duration) {
        try {
          navigator.mediaSession.setPositionState({
            duration: globalAudio.duration,
            playbackRate: globalAudio.playbackRate,
            position: globalAudio.currentTime,
          });
        } catch {}
      }
    } catch (e) {
      console.warn("seek failed", e);
    }
  }
}

export function getCurrentTrack() {
  if (!globalQueue.length || !globalAudio || globalAudio.paused) return null;
  return globalQueue[globalQueueIndex] || null;
}

function playInternal(music, queue, idx) {
  if (!music?.preview_url) {
    return false;
  }
  // Stop any previous audio
  if (globalAudio) {
    globalAudio.pause();
    try { globalAudio.src = ""; } catch {}
    globalAudio = null;
  }

  // Reuse preloaded audio if URL matches (instant playback)
  let audio;
  if (preloadAudio && preloadUrl === music.preview_url) {
    audio = preloadAudio;
    preloadAudio = null;
    preloadUrl = null;
  } else {
    audio = new Audio(music.preview_url);
  }
  audio.preload = "auto";
  audio.volume = 0.7;
  // iOS Safari: garante inline playback (não força fullscreen) e ajuda autoplay/background
  try {
    audio.setAttribute("playsinline", "true");
    audio.setAttribute("webkit-playsinline", "true");
  } catch {}

  let lastReportedPos = 0;
  audio.ontimeupdate = () => {
    emitProgress(audio.currentTime || 0, audio.duration || 0);
    // Update media session position every ~1s
    if ("mediaSession" in navigator && audio.duration && Math.abs(audio.currentTime - lastReportedPos) > 1) {
      lastReportedPos = audio.currentTime;
      try {
        navigator.mediaSession.setPositionState({
          duration: audio.duration,
          playbackRate: audio.playbackRate,
          position: audio.currentTime,
        });
      } catch {}
    }
    // Preload next track when current is past 25%
    if (audio.duration && audio.currentTime > audio.duration * 0.25) {
      preloadNext(queue, idx);
    }
  };
  audio.onloadedmetadata = () => {
    emitProgress(audio.currentTime || 0, audio.duration || 0);
    preloadNext(queue, idx);
  };
  audio.onplay = () => {
    if ("mediaSession" in navigator) {
      try { navigator.mediaSession.playbackState = "playing"; } catch {}
    }
  };
  audio.onpause = () => {
    if ("mediaSession" in navigator) {
      try { navigator.mediaSession.playbackState = "paused"; } catch {}
    }
    // Detecta se foi pausa do sistema (interrupção iOS / call / Siri) vs pausa do usuário.
    // Se o audio.ended === false E currentTime > 0 E não foi user → provavelmente interrupção.
    // Marcamos só pra tentar resumir quando o documento voltar a ser visível.
    if (!audio.ended && audio.currentTime > 0) {
      lastPauseReason = "interruption";
    }
  };
  audio.onended = () => {
    // Track completed naturally — reset skip counter so a normal playthrough doesn't trip the guard
    resetSkipCounter();
    const nextIdx = idx + 1;
    if (queue && nextIdx < queue.length) {
      playInternal(queue[nextIdx], queue, nextIdx);
    } else if (radioMode) {
      // RADIO MODE: load another random CD and keep playing
      loadRandomCDAndPlay();
    } else {
      globalAudio = null;
      globalQueue = [];
      globalQueueIndex = 0;
      emitPlayingChange(null, null);
    }
  };
  audio.onerror = () => {
    // Ignore aborts (user paused / navigated): MediaError.code === 1
    const err = audio.error;
    if (err && err.code === 1) {
      return; // MEDIA_ERR_ABORTED — normal user action, do not auto-skip
    }
    // Wait 1.5s before deciding to skip — gives the browser a chance to recover
    // from transient network blips on mobile (3G/5G).
    setTimeout(() => {
      if (audio !== globalAudio) return;
      // If we are skipping too fast (e.g. autoplay blocked on mobile),
      // stop instead of looping forever.
      if (recordSkipAndShouldAbort()) {
        console.warn("[player] too many skips in a short window — stopping");
        try { globalAudio?.pause(); } catch {}
        globalAudio = null;
        globalQueue = [];
        globalQueueIndex = 0;
        emitPlayingChange(null, null);
        resetSkipCounter();
        return;
      }
      const nextIdx = idx + 1;
      if (queue && nextIdx < queue.length) {
        playInternal(queue[nextIdx], queue, nextIdx);
      } else if (radioMode) {
        loadRandomCDAndPlay();
      } else {
        globalAudio = null;
        emitPlayingChange(null, null);
      }
    }, 1500);
  };
  const playPromise = audio.play();
  if (playPromise && typeof playPromise.then === "function") {
    playPromise.catch((err) => {
      // On mobile, autoplay is often blocked without a recent user gesture.
      // Don't trigger onerror cascade — just mark as paused and let the user
      // tap play again.
      if (err && (err.name === "NotAllowedError" || err.name === "AbortError")) {
        console.info("[player] autoplay blocked — user must tap play", err.name);
        // Pause the audio element and emit playing change as paused (keep current)
        try { audio.pause(); } catch {}
        // Don't null out globalAudio — keep current track so user can tap play to resume
      } else {
        console.warn("[player] play() rejected:", err?.message || err);
      }
    });
  }
  globalAudio = audio;
  globalQueue = queue || [music];
  globalQueueIndex = idx;
  // User intent: clicar pra tocar = quer tocar.
  userWantsPlay = true;
  lastPauseReason = null;
  // Reset skip counter on a successful user-initiated play
  resetSkipCounter();
  emitPlayingChange(music.id, music);

  // Media Session (background playback + mobile notification controls)
  updateMediaSession(music);
  if ("mediaSession" in navigator) {
    try { navigator.mediaSession.playbackState = "playing"; } catch {}
  }

  // Track plays (ignore 404 for synthetic cd-xxx ids)
  if (music.id && !music.id.startsWith("cd-")) {
    axios.post(`${API}/musics/${music.id}/play`).catch(() => {});
  }
  // Also track a generic play event if it has a source CD (for analytics)
  if (music._source_cd_id) {
    axios.post(`${API}/stats/play`, {
      cd_id: music._source_cd_id,
      track_title: music.title,
      artist_name: music.artist_name,
    }).catch(() => {});
  }
  return true;
}

export function playNext() {
  const nextIdx = globalQueueIndex + 1;
  if (globalQueue && nextIdx < globalQueue.length) {
    playInternal(globalQueue[nextIdx], globalQueue, nextIdx);
  } else if (radioMode) {
    loadRandomCDAndPlay();
  }
}

export function playPrev() {
  const prevIdx = globalQueueIndex - 1;
  if (globalQueue && prevIdx >= 0) {
    playInternal(globalQueue[prevIdx], globalQueue, prevIdx);
  }
}

export function togglePauseGlobal() {
  if (!globalAudio) return;
  if (globalAudio.paused) {
    userWantsPlay = true;
    lastPauseReason = null;
    globalAudio.play().then(() => {
      const cur = globalQueue[globalQueueIndex];
      emitPlayingChange(cur?.id || null, cur || null);
    }).catch(() => {});
  } else {
    userWantsPlay = false;
    lastPauseReason = "user";
    globalAudio.pause();
    emitPlayingChange(null, null);
  }
}

export function useAudioPlayer() {
  const [playingId, setPlayingId] = useState(null);
  const playingIdRef = useRef(null);

  if (playingIdRef.current !== playingId) {
    playingIdRef.current = playingId;
  }

  useEffect(() => {
    const unsubscribe = registerPlayingChangeCallback((id) => {
      playingIdRef.current = id;
      setPlayingId(id);
    });
    return unsubscribe;
  }, []);

  const handlePlay = useCallback((music, queueOverride = null) => {
    if (!music?.preview_url) return;

    if (playingIdRef.current === music.id && globalAudio && !globalAudio.paused) {
      globalAudio.pause();
      emitPlayingChange(null, null);
      return;
    }

    stopCallbacks.forEach((cb) => cb());

    let queue;
    let idx;
    if (queueOverride && Array.isArray(queueOverride) && queueOverride.length > 0) {
      queue = queueOverride;
      idx = queueOverride.findIndex((m) => m.id === music.id);
      if (idx < 0) idx = 0;
    } else {
      queue = [music];
      idx = 0;
    }
    playInternal(music, queue, idx);
  }, []);

  return { playingId, handlePlay };
}
